import {
    getCurrentNote,
    getCurrentNoteIndex,
    nextNote,
    getMelodyBlocks, findBlockByMeasureId, getFullMelodySequence
} from './notes.js';
import {
    getSequence,
    getCurrentGameTempo,
    getSpeedFactor,
    loadProgress,
    getGameStars,
    checkLevelCompletion, getGamingMode,
    getBlockProgress,
} from "./game.js";
import { sequencePlayer } from './sequencePlayer.js';
import { notePlayer } from './notePlayer.js';

// Константы для GuitarManager
const GUITAR_CONFIG = {
    MAX_STARS: 5,
    STAR_SYMBOL: '★',
    BLOCK_CLICK_DELAY_MS: 100
};

class GuitarManager {
    constructor() {
        this.playbackTimeout = null;
    }
    async renderMelodyBlocks() {
        const blocksContainer = document.getElementById('melodyBlocks');
        if (!blocksContainer) return;

        blocksContainer.innerHTML = '';
        const blocks = getMelodyBlocks();
        await loadProgress();
        
        blocks.forEach(block => {
            const blockElement = this.createBlockElement(block);
            blocksContainer.appendChild(blockElement);
            
            const starsCount = getGameStars(block.id) || 0;
            this.updateStarsDisplay(block.id, starsCount);
        });
    }

    createBlockElement(block) {
        const blockElement = document.createElement('div');
        blockElement.className = 'melody-block';
        blockElement.dataset.blockId = block.id;
        blockElement.style.backgroundColor = block.color;

        const minLevel = block.minGameLevel || 0;
        const maxStars = GUITAR_CONFIG.MAX_STARS;
        // Чем выше минимальный уровень, тем меньше базовых звезд.
        // Например: minLevel = 0 → 5 звезд, 1 → 4, 2 → 3, 3 → 2, 4 → 1, 5+ → 1.
        const baseStars = Math.max(1, maxStars - minLevel);

        blockElement.innerHTML = `
            <div class="block-header">
                <span>Блок ${block.id}</span>
            </div>
            <div class="block-levels">
                <div class="stars-container" data-base-stars="${baseStars}"></div>
            </div>
        `;

        return blockElement;
    }

    setActiveMelodyBlock(id) {
        document.querySelectorAll('.melody-block').forEach(el => {
            el.classList.remove('active');
        });

        const blockElement = document.querySelector(`[data-block-id="${id}"]`);
        if (blockElement) {
            blockElement.classList.add('active');
            
            // Автоскролл к активному блоку в melody-blocks
            const melodyBlocksContainer = blockElement.closest('.melody-blocks');
            if (melodyBlocksContainer) {
                blockElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'nearest'
                });
            }
        } else {
            console.warn("Block element not found for id:", id);
        }
    }

    async playSequence() {
        const tempo = getCurrentGameTempo();

        const playNextNote = async () => {
            const isGameMode = getGamingMode();
            const speedFactor = getSpeedFactor();

            const currentNoteIndex = getCurrentNoteIndex();
            const sequence = getSequence();

            // Завершаем последовательность, если дошли до конца
            if (sequence && currentNoteIndex >= sequence.length - 1) {
                await nextNote(); // установим индекс за пределы, чтобы getCurrentNote вернул null
                await checkLevelCompletion();
                return;
            }

            // Сначала проверяем следующую ноту БЕЗ переключения индекса
            const nextNoteIndex = currentNoteIndex + 1;
            const nextNoteItem = sequence[nextNoteIndex];
            
            // Проверяем, является ли следующая нота паузой
            const isNextPause = nextNoteItem && Array.isArray(nextNoteItem) && nextNoteItem[0] && nextNoteItem[0].pause === "true";

            // Если следующая нота - пауза, ждем её длительность ПЕРЕД переключением индекса
            if (isNextPause) {
                // В нулевом режиме сложности паузы пропускаются - просто переключаем индекс и продолжаем
                if (isGameMode && speedFactor === 0) {
                    await nextNote(); // Пропускаем паузу, переключаем индекс
                    await playNextNote(); // Продолжаем с следующей ноты
                    return;
                }

                // Вычисляем длительность паузы с учетом dotted
                const pauseDuration = this.calculateNoteDuration(tempo, nextNoteItem);
                
                // Ждем длительность паузы, затем переключаем индекс и продолжаем
                clearTimeout(this.playbackTimeout);
                this.playbackTimeout = setTimeout(async () => {
                    await nextNote(); // Переключаем индекс после паузы
                    await playNextNote(); // Продолжаем с следующей ноты
                }, pauseDuration);
                return;
            }

            // Если следующая нота не пауза, переключаем индекс и обрабатываем ноту
            await nextNote();
            const note = getCurrentNote();

            if (!note) {
                clearTimeout(this.playbackTimeout);
                this.playbackTimeout = null;
                return;
            }

            // Проверяем, является ли текущая нота паузой
            const isCurrentPause = Array.isArray(note) && note[0] && note[0].pause === "true";
            
            // В нулевом режиме сложности паузы пропускаются
            if (isCurrentPause && isGameMode && speedFactor === 0) {
                // Пропускаем паузу, переходим к следующей ноте
                await playNextNote();
                return;
            }

            // В неигровом режиме воспроизводим подсказку
            if (!isGameMode) {
                notePlayer.playNote(note);
            }

            // В нулевом режиме сложности ноты не переключаются автоматически
            if (isGameMode && speedFactor === 0) {
                clearTimeout(this.playbackTimeout);
                this.playbackTimeout = null;
                return;
            }

            // Автоматический переход для всех остальных режимов
            const noteDuration = this.calculateNoteDuration(tempo, note);

            clearTimeout(this.playbackTimeout);
            this.playbackTimeout = setTimeout(playNextNote, noteDuration);
        };

        await playNextNote();
    }

    getNoteDuration(note) {
        const DEFAULT_DURATION = 4;
        
        if (Array.isArray(note)) {
            return note[0]?.duration || DEFAULT_DURATION;
        }
        return note?.duration || DEFAULT_DURATION;
    }

    calculateNoteDuration(tempo, note) {
        const DEFAULT_DURATION = 4;
        
        // Извлекаем duration и dotted из ноты
        let duration, isDotted;
        
        if (Array.isArray(note)) {
            // Если это аккорд или пауза, берем первый элемент
            duration = note[0]?.duration || DEFAULT_DURATION;
            // Обрабатываем dotted как булево значение или строку "true"/"false"
            const dottedValue = note[0]?.dotted;
            isDotted = dottedValue === true || dottedValue === "true" || dottedValue === 1;
        } else {
            duration = note?.duration || DEFAULT_DURATION;
            // Обрабатываем dotted как булево значение или строку "true"/"false"
            const dottedValue = note?.dotted;
            isDotted = dottedValue === true || dottedValue === "true" || dottedValue === 1;
        }
        
        const QUARTER_NOTE_MS = 60000 / tempo;
        const QUARTER_NOTES_PER_BEAT = 4;
        let baseDuration = QUARTER_NOTE_MS * (QUARTER_NOTES_PER_BEAT / duration);
        
        // Если нота dotted, удлиняем её в 1.5 раза
        if (isDotted) {
            baseDuration *= 1.5;
        }
        
        return baseDuration;
    }

    async stopSequence() {
        if (this.playbackTimeout) {
            clearTimeout(this.playbackTimeout);
            this.playbackTimeout = null;
        }
    }

    highlightMelodyBlock(id) {
        if (id === null || id === undefined) {
            console.warn("Invalid block id:", id);
            return;
        }

        const block = findBlockByMeasureId(id);
        if (block) {
            setActiveMelodyBlock(block.id);
        } else {
            console.warn("Block not found for measure id:", id);
            setActiveMelodyBlock(id);
        }
    }

    updateStarsDisplay(blockId, starsCount) {
        const blockElement = document.querySelector(`.melody-block[data-block-id="${blockId}"]`);
        if (!blockElement) return;

        const starsContainer = blockElement.querySelector('.stars-container');
        if (!starsContainer) return;

        const baseStars = Number(starsContainer.dataset.baseStars || GUITAR_CONFIG.MAX_STARS);

        // Если блок начинается не с нулевого уровня, ограничиваем максимум видимых звезд.
        const effectiveMaxStars = Math.min(baseStars, GUITAR_CONFIG.MAX_STARS);
        const clampedStars = Math.max(0, Math.min(starsCount, effectiveMaxStars));

        const starsHTML = this.createStarsHTML(clampedStars, effectiveMaxStars);
        starsContainer.innerHTML = starsHTML;
    }

    createStarsHTML(starsCount, maxStars = GUITAR_CONFIG.MAX_STARS) {
        let starsHTML = '';
        for (let i = 0; i < maxStars; i++) {
            const filledClass = i < starsCount ? 'filled' : '';
            starsHTML += `<span class="star ${filledClass}">${GUITAR_CONFIG.STAR_SYMBOL}</span>`;
        }
        return starsHTML;
    }

    renderFullMelodySequence() {
        const container = document.getElementById('fullMelodySequence');
        if (!container) return;

        container.innerHTML = '';
        const fullSequence = getFullMelodySequence();
        const blockProgress = getBlockProgress() || {};

        fullSequence.forEach((block, index) => {
            const blockElement = this.createSequenceBlockElement(block, index, blockProgress);
            container.appendChild(blockElement);
        });
        
        // Проверяем и обновляем состояние кнопки "Проиграть все"
        this.updatePlayAllButton();
    }

    createSequenceBlockElement(block, index, blockProgress = {}) {
        const blockElement = document.createElement('div');
        blockElement.className = 'sequence-block';
        blockElement.dataset.sequenceIndex = index;
        blockElement.dataset.blockId = block.id;
        blockElement.dataset.isPause = block.isPause;

        if (block.isPause) {
            blockElement.classList.add('sequence-block-pause');
        } else {
            blockElement.classList.add('sequence-block-normal');
            
            // На мобильных устройствах всегда показываем цветные блоки
            const isMobile = window.innerWidth <= 768 || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
            
            // Проверяем, пройден ли блок
            const progress = blockProgress[block.id];
            const isCompleted = progress && progress.completed === 1;
            
            if (isCompleted || isMobile) {
                // Блок пройден или мобильное устройство - используем основной цвет
                blockElement.style.backgroundColor = block.color;
                blockElement.style.opacity = '1';
                blockElement.classList.add('sequence-block-completed');
            } else {
                // Блок не пройден - серый цвет
                blockElement.style.backgroundColor = '#555555';
                blockElement.style.opacity = '0.5';
                blockElement.classList.add('sequence-block-empty');
            }
            
            // Обработчик клика теперь устанавливается в setupBlockClickHandlers()
            // Не добавляем обработчик здесь, чтобы избежать дублирования
        }

        return blockElement;
    }
    
    // Обновляет визуальное состояние блоков в полной последовательности
    updateFullMelodySequenceBlocks() {
        const container = document.getElementById('fullMelodySequence');
        if (!container) return;
        
        const blockProgress = getBlockProgress() || {};
        const blockElements = container.querySelectorAll('.sequence-block[data-block-id]');
        
        // На мобильных устройствах всегда показываем цветные блоки
        const isMobile = window.innerWidth <= 768 || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        
        blockElements.forEach(element => {
            const blockId = parseInt(element.dataset.blockId);
            const isPause = element.dataset.isPause === 'true';
            
            if (isPause) return;
            
            const progress = blockProgress[blockId];
            const isCompleted = progress && progress.completed === 1;
            
            // Находим оригинальный цвет блока из последовательности
            const fullSequence = getFullMelodySequence();
            const block = fullSequence.find(b => b.id === blockId && !b.isPause);
            
            if ((isCompleted || isMobile) && block) {
                // Блок пройден или мобильное устройство - используем основной цвет
                element.style.backgroundColor = block.color;
                element.style.opacity = '1';
                element.classList.remove('sequence-block-empty');
                element.classList.add('sequence-block-completed');
            } else {
                // Блок не пройден - серый цвет
                element.style.backgroundColor = '#555555';
                element.style.opacity = '0.5';
                element.classList.remove('sequence-block-completed');
                element.classList.add('sequence-block-empty');
            }
        });
        
        // Проверяем и обновляем состояние кнопки "Проиграть все"
        this.updatePlayAllButton();
    }
    
    // Проверяет, все ли блоки пройдены, и показывает/скрывает кнопки управления
    updatePlayAllButton() {
        const controlsContainer = document.querySelector('.top-bar-center-controls');
        if (!controlsContainer) return;
        
        const fullSequence = getFullMelodySequence();
        const blockProgress = getBlockProgress() || {};
        
        // Получаем уникальные ID блоков (исключая паузы)
        const uniqueBlockIds = [...new Set(
            fullSequence
                .filter(block => !block.isPause)
                .map(block => block.id)
        )];
        
        // Проверяем, все ли блоки пройдены
        const allBlocksCompleted = uniqueBlockIds.every(blockId => {
            const progress = blockProgress[blockId];
            return progress && progress.completed === 1;
        });
        
        // Показываем/скрываем контейнер с кнопками
        if (allBlocksCompleted && uniqueBlockIds.length > 0) {
            controlsContainer.style.display = 'flex';
        } else {
            controlsContainer.style.display = 'none';
        }
        
        const playAllBtn = document.getElementById('playAllBtn');
        if (playAllBtn && allBlocksCompleted && uniqueBlockIds.length > 0) {
            // Все блоки пройдены - активируем кнопку
            playAllBtn.disabled = false;
            playAllBtn.classList.remove('btn-disabled');
        } else {
            // Не все блоки пройдены - деактивируем кнопку
            playAllBtn.disabled = true;
            playAllBtn.classList.add('btn-disabled');
        }
    }

}

// Создаем и экспортируем экземпляр
const guitarManager = new GuitarManager();

// Убедимся, что все методы правильно привязаны к экземпляру
/** Рендерит кликабельные блоки мелодии и отображает их звезды */
export const renderMelodyBlocks = guitarManager.renderMelodyBlocks.bind(guitarManager);
/** Подсвечивает активный блок мелодии */
export const setActiveMelodyBlock = guitarManager.setActiveMelodyBlock.bind(guitarManager);
/** Запускает поочередное проигрывание нот в текущем режиме */
export const playSequence = guitarManager.playSequence.bind(guitarManager);
/** Останавливает очередность проигрывания нот */
export const stopSequence = guitarManager.stopSequence.bind(guitarManager);
/** Включает/выключает подсветку блока по его id */
export const highlightMelodyBlock = guitarManager.highlightMelodyBlock.bind(guitarManager);
/** Обновляет отображение рейтинга (звезд) для блока */
export const updateStarsDisplay = guitarManager.updateStarsDisplay.bind(guitarManager);
/** Рендерит полную последовательность блоков мелодии */
export const renderFullMelodySequence = guitarManager.renderFullMelodySequence.bind(guitarManager);
/** Обновляет визуальное состояние блоков в полной последовательности на основе прогресса */
export const updateFullMelodySequenceBlocks = guitarManager.updateFullMelodySequenceBlocks.bind(guitarManager);